import { upsertRoll } from "@/pages/rollshop/repositories/OperationRepository";
import { Section } from "@/shared/models/Section";
import { Stand } from "@/shared/models/Stand";
import { setSectionsToPart } from "@/shared/repositories/SectionRepository";
import { setStandsToPart } from "@/shared/repositories/StandRepository";
import { useSectionsByPart } from "@/shared/selectors/SectionsOfPartSelector";
import { useStandsByPart } from "@/shared/selectors/StandsOfPartSelector";
import { ErrorModal, getMomentTz } from "@dexteel/mesf-core";
import { Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { ChangeEvent, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
	OPERATION_INITIAL_VALUES,
	RollOperation,
} from "../../../models/Operation";
import { ConfirmActionModal } from "./ConfirmActionModal";

const moment = getMomentTz();

type Props = {
	roll: RollOperation | null;
	modal: "create" | "update" | "delete" | "";
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const OperationDetails = ({ roll, modal, onHide }: Props) => {
	const navigate = useNavigate();

	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
	const [standsSelected, setStandsSelected] = useState<Stand[]>([]);
	const [sectionsSelected, setSectionsSelected] = useState<Section[]>([]);
	const [selectedAction, setSelectedAction] = useState<
		"Cutting" | "Dressing" | "Spray Welding" | "Cleaning" | ""
	>("");
	const [showConfirmModal, setShowConfirmModal] = useState(false);

	const { control, reset, handleSubmit, getValues } = useForm<RollOperation>({
		defaultValues: OPERATION_INITIAL_VALUES,
		mode: "onChange",
	});

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertRoll, {
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["operations"] });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});
	const sectionsMutation = useMutation(
		(params: { partId: number; sectionsSelected: Section[] }) =>
			setSectionsToPart(params.partId, params.sectionsSelected),
		{
			meta: { successMessage: "The sections were saved successfully" },
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: ["operations"] });
			},
			onError: (error: Error) => {
				setError(error.message);
			},
			onSettled: () => setIsSubmitLoading(false),
		},
	);
	const standsMutation = useMutation(
		(params: { partId: number; standsSelected: Stand[] }) =>
			setStandsToPart(params.partId, params.standsSelected),
		{
			meta: { successMessage: "The stands were saved successfully" },
			onSuccess: async () => {
				await queryClient.invalidateQueries({ queryKey: ["operations"] });
			},
			onError: (error: Error) => {
				setError(error.message);
			},
			onSettled: () => setIsSubmitLoading(false),
		},
	);

	const { data: sectionsOfPart } = useSectionsByPart({
		partId: roll?.PartId ?? 0,
		onError: (error) => setError(error.message),
	});
	const { data: standsOfPart } = useStandsByPart({
		partId: roll?.PartId ?? 0,
		onError: (error) => setError(error.message),
		onSuccess: (data) => {
			if (data && data.length > 0) {
				setStandsSelected([...data]);
			}
		},
	});

	const onSubmit: SubmitHandler<RollOperation> = async (data) => {
		setIsSubmitLoading(true);
		upsertMutation.mutate({
			partId: data?.PartId,
			locationId: data?.LocationId ?? null,
			diameter: data.Diameter,
		});
	};

	const onPressedAction = (
		action: "Cutting" | "Dressing" | "Spray Welding" | "Cleaning",
	) => {
		setSelectedAction(action);
		setShowConfirmModal(true);
	};

	const onChangeRealType = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		onChange: (value: string) => void,
	) => {
		const value = e.target.value.replace(",", ".");
		if (value === "" || /^(\d*\.?\d{0,3}|\d+\.)$/.test(value)) {
			onChange(value);
		}
	};
	const onBlurRealType = (onChange: (value: string) => void, value: string) => {
		const numValue = parseFloat(value);
		if (!isNaN(numValue)) {
			onChange(numValue.toFixed(3));
		} else if (value === "") {
			onChange("");
		}
	};

	useEffect(() => {
		if (modal !== "") {
			reset(roll || OPERATION_INITIAL_VALUES);
			setIsSubmitLoading(false);
		}
	}, [modal, roll]);

	return (
		<>
			{roll === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a roll or create a new one</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					{/* <Grid2 container spacing={1} justifyContent="center">
						<Grid2 container spacing={3} size={{md: 12}} alignItems="center">
							<Grid2 size={{md:12}} sm={12} size={{xs:12}}>
								<Controller
									name="PartName"
									control={control}
									rules={{ required: "Name is required" }}
									render={({ field }) => (
										<TextField
											label="Name"
											variant="outlined"
											{...field}
											size="small"
											fullWidth
											disabled
											InputProps={{
												endAdornment: (
													<IconButton
														onClick={() =>
															navigate(
																`/rollshop-${
																	roll?.RollTypeCode === "U"
																		? "universal-rolls"
																		: "edger-rolls"
																}/${roll?.PartId}`,
															)
														}
														size="small"
														title={`See in ${
															roll?.RollTypeCode === "U" ? "Universal" : "Edger"
														} Rolls`}
													>
														<VisibilityIcon />
													</IconButton>
												),
											}}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{md:3}} sm={12} size={{xs:12}}>
								<Typography>Cutting</Typography>
							</Grid2>
							<Grid2 size={{md: 5}} sm={12} size={{xs:12}}>
								<Controller
									name="LastCuttingTime"
									control={control}
									render={({ field }) => (
										<DatePicker
											disabled
											label="Last Cutting Time"
											format="MM/DD/YYYY HH:mm:ss"
											value={field.value ? moment(field.value) : null}
											slotProps={{
												textField: {
													size: "small",
													error: !!error,
												},
											}}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{md:4}} sm={12} size={{xs:12}}>
								<Button
									fullWidth
									variant="contained"
									color="primary"
									onClick={() => {
										onPressedAction("Cutting");
									}}
								>
									Cutting Done
								</Button>
							</Grid2>
						</Grid2>
						<Grid2 container spacing={3} size={{md: 12}} alignItems="center">
							<Grid2 size={{md:3}} sm={12} size={{xs:12}}>
								<Typography>Dressing</Typography>
							</Grid2>
							<Grid2 size={{md: 5}} sm={12} size={{xs:12}}>
								<Controller
									name="LastDressingTime"
									control={control}
									render={({ field }) => (
										<DatePicker
											disabled
											label="Last Dressing Time"
											format="MM/DD/YYYY HH:mm:ss"
											value={field.value ? moment(field.value) : null}
											slotProps={{
												textField: {
													size: "small",
													error: !!error,
												},
											}}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{md:4}} sm={12} size={{xs:12}}>
								<Button
									fullWidth
									variant="contained"
									color="primary"
									onClick={() => {
										onPressedAction("Dressing");
									}}
								>
									Dressing Done
								</Button>
							</Grid2>
						</Grid2>
						<Grid2 container spacing={3} size={{md: 12}} alignItems="center">
							<Grid2 size={{md:3}} sm={12} size={{xs:12}}>
								<Typography>Spray Welding</Typography>
							</Grid2>
							<Grid2 size={{md: 5}} sm={12} size={{xs:12}}>
								<Controller
									name="LastSprayWeldingTime"
									control={control}
									render={({ field }) => (
										<DatePicker
											disabled
											label="Last Spray Welding Time"
											format="MM/DD/YYYY HH:mm:ss"
											value={field.value ? moment(field.value) : null}
											slotProps={{
												textField: {
													size: "small",
													error: !!error,
												},
											}}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{md:4}} sm={12} size={{xs:12}}>
								<Button
									fullWidth
									variant="contained"
									color="primary"
									onClick={() => {
										onPressedAction("Spray Welding");
									}}
								>
									Spray Welding Done
								</Button>
							</Grid2>
						</Grid2>
						<Grid2 container spacing={3} size={{md: 12}} alignItems="center">
							<Grid2 size={{md:3}} sm={12} size={{xs:12}}>
								<Typography>Cleaning</Typography>
							</Grid2>
							<Grid2 size={{md: 5}} sm={12} size={{xs:12}}>
								<Controller
									name="LastCleaningTime"
									control={control}
									render={({ field }) => (
										<DatePicker
											disabled
											label="Last Cleaning Time"
											format="MM/DD/YYYY HH:mm:ss"
											value={field.value ? moment(field.value) : null}
											slotProps={{
												textField: {
													size: "small",
													error: !!error,
												},
											}}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{md:4}} sm={12} size={{xs:12}}>
								<Button
									fullWidth
									variant="contained"
									color="primary"
									onClick={() => {
										onPressedAction("Cleaning");
									}}
								>
									Cleaning Done
								</Button>
							</Grid2>
						</Grid2>
						<Grid2
							item
							size={{xs:12}}
							size={{md: 12}}
							style={{ margin: "20px 0", padding: "0 30px" }}
						>
							<Divider variant="middle" />
						</Grid2>
						<Grid2 container spacing={3} size={{md: 12}}>
							<Grid2 size={{md: 12, xs: 12}}>
								<Controller
									name="SectionsId"
									control={control}
									render={() => (
										<SectionField
											label="Sections"
											onSelectionChanged={setSectionsSelected}
											onSaved={() => {
												sectionsMutation.mutate({
													partId: roll?.PartId!,
													sectionsSelected,
												});
											}}
											sectionsOfPart={sectionsSelected}
											partId={roll?.PartId!}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{md: 12, xs: 12}}>
								<Controller
									name="StandsId"
									control={control}
									render={() => (
										<StandField
											label="Stands"
											onSelectionChanged={setStandsSelected}
											onSaved={() => {
												standsMutation.mutate({
													partId: roll?.PartId!,
													standsSelected: standsSelected,
												});
											}}
											standsOfPart={standsSelected}
											partId={roll?.PartId!}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{xs:12}} size={{md: 2}}>
								<Controller
									name="Diameter"
									control={control}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											label="Current Diam."
											variant="outlined"
											fullWidth
											size="small"
											autoComplete="off"
											value={value ?? ""}
											onChange={(e) => {
												onChangeRealType(e, onChange);
											}}
											onBlur={() => {
												onBlurRealType(onChange, String(value));
												upsertMutation.mutate(
													{
														partId: roll?.PartId!,
														locationId: roll?.LocationId ?? null,
														rollPosition: getValues("RollPosition"),
														diameter: value,
													},
													{
														onSuccess: () =>
															enqueueSnackbar(
																"The current diameter was saved successfully",
																{ variant: "success" },
															),
													},
												);
											}}
											disabled={modal === "delete"}
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{xs:12}} size={{md: 2}}>
								<Controller
									name="AccumulatedBillets"
									control={control}
									render={({ field: { value } }) => (
										<TextField
											label="Accum. Billets"
											variant="outlined"
											fullWidth
											size="small"
											autoComplete="off"
											value={value ?? ""}
											disabled
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{xs:12}} size={{md: 2}}>
								<Controller
									name="AccumulatedTons"
									control={control}
									render={({ field: { value } }) => (
										<TextField
											label="Accum. Tons"
											variant="outlined"
											fullWidth
											size="small"
											autoComplete="off"
											value={value ?? ""}
											disabled
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{xs:12}} md={6}>
								<Controller
									name="RollPosition"
									control={control}
									render={({ field: { value, onChange } }) => (
										<RollPositionToggleButton
											value={value}
											onChange={(position) => {
												onChange(position);
												upsertMutation.mutate(
													{
														partId: roll?.PartId!,
														locationId: roll?.LocationId ?? null,
														rollPosition: position,
														diameter: getValues("Diameter"),
													},
													{
														onSuccess: () =>
															enqueueSnackbar(
																"The roll position was saved successfully",
																{ variant: "success" },
															),
													},
												);
											}}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</Grid2> */}
				</form>
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
			<ConfirmActionModal
				action={selectedAction}
				roll={roll}
				show={showConfirmModal}
				onHide={(updated) => {
					setShowConfirmModal(false);
					onHide({
						shouldUpdate: updated,
						entityId: roll?.PartId,
						close: false,
					});
				}}
			/>
		</>
	);
};
